import { FC, useMemo, useState, useEffect } from 'react'
import cn from 'classnames'
import styles from 'components/features/coaching/advisorReviewPage/AdvisorReviewPage/AdvisorReviewPage.styl'
import Button from 'components/features/pfm/Button/Button'
import { FormProvider, useForm } from 'react-hook-form'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { MeetingUid } from 'components/features/coaching/advisorPage/types/interfaces'
import { AdvisorsPatchReviewAction } from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import CommonLayout from 'components/layouts/CommonLayout'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'
import Anchor from 'components/atoms/navigation/Anchor'
import CardLayout from 'components/layouts/CardLayout'
import { StarsPickerController } from 'components/features/coaching/advisorReviewPage/StarsPicker/StarsPicker'
import { setUnexpectedErrorActionCreator } from 'actions/globalActions'
import Checkbox from 'components/atoms/inputs/Checkbox'
import { AdvisorHeaderWith } from 'components/features/coaching/advisor/AdvisorHeaderWith/AdvisorHeaderWith'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { GetMeetingVideoDetailsAction } from 'components/features/coaching/advisorVideoCall/types/actions'

export type ReviewFormFields = {
  meeting_uid: MeetingUid
  rating: number
  text: string
  is_anonymous: boolean
}

export const AdvisorReviewPage: FC = () => {
  const meeting_uid: MeetingUid | null = useMemo(() => new URLSearchParams(location.search).get('meeting_uid') || null, [])
  const isDetailsLoaded = useStoreSelector((state) => !!state.data.advisors.videoCallDetails.details)

  const dispatch = useStoreDispatch()
  useMemo(() => {
    if (!meeting_uid) {
      dispatch(setUnexpectedErrorActionCreator('page-not-found'))
      return
    }

    if (!isDetailsLoaded) {
      dispatch(new GetMeetingVideoDetailsAction({ meetingId: meeting_uid }))
    }
  }, [dispatch, meeting_uid, isDetailsLoaded])

  if (!meeting_uid) {
    return null
  }

  return (
    <AdvisorReviewPageInner meeting_uid={ meeting_uid } />
  )
}

type AdvisorReviewPageInnerProps = {
  meeting_uid: MeetingUid
}
const AdvisorReviewPageInner: FC<AdvisorReviewPageInnerProps> = (props) => {
  const { meeting_uid } = props
  const initialRating = useMemo(() => +(new URLSearchParams(location.search).get('rating') || 0), [])

  const formContext = useForm<ReviewFormFields>({
    defaultValues: {
      ...(initialRating ? { rating: initialRating } : {}),
    },
  })
  const { register, handleSubmit, watch, formState: { errors }} = formContext
  const dispatch = useStoreDispatch()
  const [submittedState, setSubmittedState] = useState(false)
  const reviewRequest = useStoreSelector((state) => state.data.advisors.reviewRequest)
  const formStep = useMemo(() => {
    if (submittedState && !reviewRequest.isLoading && !reviewRequest.errorCode) {
      return 'result'
    }
    return 'form'
  }, [reviewRequest.errorCode, reviewRequest.isLoading, submittedState])

  const handleSuccessSubmit = useMemo(() => handleSubmit((fields) => {
    setSubmittedState(true)
    dispatch(new AdvisorsPatchReviewAction({
      ...fields,
      meeting_uid,
    }))
  }), [handleSubmit, dispatch, meeting_uid])

  const rating = watch('rating')

  useEffect(() => {
    if (!rating) {
      return
    }
    dispatch(new AdvisorsPatchReviewAction({
      meeting_uid,
      rating,
    }))

  }, [rating, dispatch, meeting_uid])

  const advisorUid = useStoreSelector((state) => {
    return state.data.advisors.videoCallDetails?.details?.advisor?.uid
  })

  const advisorChild = useMemo(() => {
    if (!advisorUid) {
      return
    }
    return <AdvisorHeaderWith uid={ advisorUid } vertical />
  }, [advisorUid])

  const formChild = useMemo(() => formStep === 'form' && (
    <FormProvider { ...formContext }>
      <form onSubmit={ handleSuccessSubmit } className={ styles.reviewForm }>
        <fieldset>
          <label>Your Rating</label>
          <StarsPickerController name='rating' rules={{ required: true }} />
          {!!errors.rating && <small className={ styles.error }>Please rate your coach 1-5 stars</small> }
        </fieldset>
        <fieldset>
          <label>Your Review</label>
          <textarea className={ cn({ [styles.error]: !!errors.text }) } { ...register('text', { required: true }) } />
          { !!errors.text && <small className={ styles.error }>Please write something about your session</small> }
          <small>
            This review may be posted publicly. Please don't include any personal information.
          </small>
        </fieldset>
        <Button submit fullWidth text='Submit Review' />
        { !!reviewRequest.errorCode && (
          <fieldset>
            <small className={ styles.error }>Something went wrong. Please try again or contact support</small>
          </fieldset>
        )}
        <fieldset>
          <label className={ styles.checkbox }>
            <Checkbox
              register={ register }
              label='Hide your first name and last initial from this review.'
              name='is_anonymous'
            />
          </label>
        </fieldset>
      </form>
    </FormProvider>
  ), [handleSuccessSubmit, formContext, formStep, errors, register, reviewRequest.errorCode])

  const confirmationChild = useMemo(() => formStep === 'result' && (
    <p className={ styles.subtitle }>Your review has been submitted.</p>
  ), [formStep])

  const backToStatusButton = (
    <NavigationLink to='coaching'>
      <Button style='hollow' size='small' text='Go To Status' />
    </NavigationLink>
  )

  return (
    <CommonLayout headerButtons={ [backToStatusButton] }>
      <PageMeta
        title='Rate Your Coach | Status Money'
        description={ `Leave a review for your sessions' coach` }
      />
      <Anchor placement='login' />

      <CardLayout title={ !submittedState ? 'Rate Your Coach' : 'Thank you for \n your review' }>
        <p className={ styles.subtitle }>Your review helps us match you <br /> with great coaches.</p>
        <div className={ styles.advisorWrapper }>{ advisorChild }</div>
        <hr className={ styles.divider } />
        { formChild }
        { confirmationChild }
      </CardLayout>
    </CommonLayout>
  )
}
